import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerQuoteFonts } from "@/lib/pdfFont";
import { NUM, fmtDate, unitLabel, docFileName } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { loadCompanyLogo, logoAspect } from "@/lib/branding";
import { loadIcons } from "@/lib/pdfIcons";
import { loadBrandLogos } from "@/lib/brandLogos";
import { resolveQuoteTerms, quoteVatExempt, itemDisplayName, itemSubLines } from "@/components/QuoteTemplate";

// Palet: lacivert + altın vurgu (kullanıcının referans şablonu)
const NAVY = [15, 39, 69];
const NAVY_HEX = "#0F2745";
const GOLD = [242, 183, 5];
const GOLD_D = [196, 144, 0];
const INK = [30, 41, 59];
const TEXT = [51, 65, 85];
const SLATE = [100, 116, 139];
const LINE = [222, 228, 236];
const WASH = [245, 247, 250];
const BAND = [240, 243, 248];
const WHITE = [255, 255, 255];
const money = (v) => `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} ₺`;
const norm = (s) => String(s || "").trim().toLocaleUpperCase("tr");
const titleTr = (s) => String(s || "").toLocaleLowerCase("tr").replace(/(^|[\s/,.\-(])(\S)/g, (m, a, b) => a + b.toLocaleUpperCase("tr"));

// Tek sayfa modunda denenen (font, kompakt) kademeleri — sığana kadar küçülür.
const FIT_STEPS = [[8, false], [7.5, false], [7, false], [6.5, true], [6, true], [5.5, true], [5, true], [4.5, true], [4, true]];

/** Teklif PDF'i — vektörel jsPDF (gömülü Chivo / IBM Plex Sans / JetBrains Mono, Lucide ikonları, orijinal marka logoları).
 *  Sayfa 1: tam başlık + marka şeridi · devam sayfaları: kompakt başlık · her sayfada altın çizgili alt bilgi. */
export async function buildQuotePdf(q, opts = {}) {
  const { detailed = true, showCodes = false, showBrand = true, showBrands = true, terms = [], singlePage = false } = opts;
  const [logo, icons, brands] = await Promise.all([loadCompanyLogo(), loadIcons(NAVY_HEX), showBrands ? loadBrandLogos() : Promise.resolve([])]);
  const AR = logoAspect();
  const vatExempt = quoteVatExempt(q);
  const kdvOn = Number(q.kdv_rate) > 0 && !vatExempt;
  const rate = Number(q.kdv_rate) > 0 ? q.kdv_rate : 20;
  const conds = resolveQuoteTerms(q, terms);
  const items = q.items || [];
  const logos = (brands || []).filter(Boolean);

  const render = async (base, compact) => {
    const pdf = new jsPDF("p", "mm", "a4");
    const F = await registerQuoteFonts(pdf);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const M = 12, W = pageW - M * 2, FOOT = 12, HEAD1 = compact ? 66 : 74, HEAD2 = 22, STRIP = logos.length ? (compact ? 11 : 13) : 0;
    const k = compact ? 0.82 : 1;
    const drawn = new Set();
    const T = (font, style, size, color) => { pdf.setFont(font, style); pdf.setFontSize(size); pdf.setTextColor(...color); };
    const fill = (c) => pdf.setFillColor(...c);
    const stroke = (c, w = 0.3) => { pdf.setDrawColor(...c); pdf.setLineWidth(w); };
    const poly = (pts, color) => { fill(color); pdf.lines(pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]), pts[0][0], pts[0][1], [1, 1], "F", true); };
    const icon = (name, x, y, s) => { if (icons[name]) { try { pdf.addImage(icons[name], "PNG", x, y, s, s); } catch { /* noop */ } } };
    const image = (data, x, y, w, h) => { try { pdf.addImage(data, "PNG", x, y, w, h); return true; } catch { return false; } };
    const shrink = (text, maxW, font, style, size, min) => { let fs = size; T(font, style, fs, INK); while (pdf.getTextWidth(text) > maxW && fs > min) { fs -= 0.3; pdf.setFontSize(fs); } return fs; };
    const ellipsize = (text, maxW) => { let s = String(text || ""); if (pdf.getTextWidth(s) <= maxW) return s; while (s.length > 1 && pdf.getTextWidth(`${s}…`) > maxW) s = s.slice(0, -1); return `${s.trimEnd()}…`; };
    const spaced = (text, x, y, size, maxW, align = "left") => {
      pdf.setFontSize(size);
      const tw = pdf.getTextWidth(text);
      const cs = Math.max(0, Math.min(0.9, (maxW - tw) / Math.max(1, text.length - 1)));
      const total = tw + cs * (text.length - 1);
      pdf.text(text, align === "right" ? x - total : x, y, { charSpace: cs });
      return total;
    };

    const drawFooter = () => {
      fill(GOLD); pdf.rect(M, pageH - FOOT + 0.6, W, 0.5, "F");
      T(F.head, "normal", 6.4, NAVY); pdf.text(COMPANY.name, M, pageH - FOOT + 5.4);
      T(F.body, "normal", 5.6, SLATE); pdf.text(titleTr(COMPANY.address), M, pageH - FOOT + 8.7);
      T(F.body, "bold", 6, NAVY); pdf.text(`${COMPANY.email}   ·   ${COMPANY.phone}`, pageW - M, pageH - FOOT + 5.4, { align: "right" });
    };

    const drawBrandStrip = () => {
      fill(GOLD); pdf.rect(0, HEAD1 + STRIP, pageW, 0.9, "F");
      if (!STRIP) return;
      stroke(LINE, 0.35); pdf.line(0, HEAD1, pageW, HEAD1);
      const maxW = compact ? 19 : 21, minGap = 3.4;
      let h0 = compact ? 4.8 : 5.4, sizes = [];
      for (; h0 >= 3; h0 -= 0.2) {
        sizes = logos.map((b) => { const ar = b.w / b.h; const w = Math.min(ar * h0, maxW); return { w, h: w / ar }; });
        if (sizes.reduce((s, z) => s + z.w, 0) + minGap * (logos.length - 1) <= W) break;
      }
      const total = sizes.reduce((s, z) => s + z.w, 0);
      const gap = Math.min((W - total) / Math.max(1, logos.length - 1), 10);
      let x = M + (W - total - gap * (logos.length - 1)) / 2;
      const cy = HEAD1 + STRIP / 2;
      logos.forEach((b, i) => {
        image(b.data, x, cy - sizes[i].h / 2, sizes[i].w, sizes[i].h);
        if (i < logos.length - 1) { stroke(LINE, 0.3); pdf.line(x + sizes[i].w + gap / 2, HEAD1 + 3.2, x + sizes[i].w + gap / 2, HEAD1 + STRIP - 3.2); }
        x += sizes[i].w + gap;
      });
    };

    const drawHeader = (pageNo) => {
      if (drawn.has(pageNo)) return;
      drawn.add(pageNo);
      if (pageNo === 1) {
        // Dekoratif çapraz bantlar (sağ üst) + altın/lacivert köşe (sağ alt)
        poly([[pageW - 92, 0], [pageW - 58, 0], [pageW, 29], [pageW, 46]], BAND);
        poly([[pageW - 25, 0], [pageW - 21.5, 0], [pageW, 10.75], [pageW, 12.5]], GOLD);
        poly([[pageW - 31, 0], [pageW - 28.5, 0], [pageW, 14.25], [pageW, 15.5]], NAVY);
        poly([[pageW - 30, HEAD1], [pageW, HEAD1], [pageW, HEAD1 - 14]], NAVY);
        poly([[pageW - 22, HEAD1], [pageW, HEAD1], [pageW, HEAD1 - 10]], GOLD);
        // Sol: logo + şirket bilgileri (ikonlu satırlar) — belirgin
        const lh = compact ? 19 : 23, lw = Math.min(lh * AR, 92);
        if (!logo || !image(logo, M, 7, lw, lh)) { T(F.head, "bold", 19, NAVY); pdf.text(COMPANY.name, M, 7 + lh * 0.65); }
        const infoFs = compact ? 7.6 : 8.5, step = infoFs * 0.5, availW = 118 - M - 10.8;
        let iy = 7 + lh + (compact ? 7.5 : 9);
        const rows = [["pin", titleTr(COMPANY.address)], ["phone", `${COMPANY.phone}   |   ${COMPANY.salesRep}`], ["mail", COMPANY.email], ["building", `Vergi Dairesi: ${titleTr(COMPANY.taxOffice)}`], ["id", `TCKN: ${COMPANY.tckn}`]];
        rows.forEach(([ic, txt]) => {
          T(F.body, "normal", infoFs, INK);
          const lines = pdf.splitTextToSize(txt, availW).slice(0, 2);
          icon(ic, M + 0.2, iy - 3.6, 4.6);
          pdf.text(lines, M + 7.8, iy, { lineHeightFactor: 1.42 });
          iy += lines.length * step + 1.6;
        });
        stroke(LINE, 0.35); pdf.line(118, 10, 118, HEAD1 - 7);
        // Sağ: TEKLİF + metin boyunca altın çizgi + slogan
        const tfs = compact ? 32 : 36, ty = compact ? 34 : 37;
        T(F.head, "bold", tfs, NAVY);
        const tw = pdf.getTextWidth("TEKLİF") + 5;
        pdf.text("TEKLİF", pageW - M, ty, { align: "right", charSpace: 1 });
        const lx = pageW - M - tw;
        fill(GOLD); pdf.rect(lx, ty + 3.6, tw, 1.6, "F");
        T(F.body, "normal", 6.4, SLATE);
        spaced("GÜVENİLİR MARKALARLA", lx, ty + 11.2, 6.4, tw);
        spaced("PROJELERİNİZE DEĞER KATARIZ.", lx, ty + 15.8, 6.4, tw);
        drawBrandStrip();
      } else {
        const lh = 10, lw = Math.min(lh * AR, 42);
        if (logo) image(logo, M, 5, lw, lh);
        T(F.head, "bold", 13, NAVY); pdf.text("TEKLİF", pageW - M, 11.5, { align: "right", charSpace: 0.6 });
        T(F.head, "bold", 7.4, GOLD_D); const no = String(q.quote_no || ""); pdf.text(no, pageW - M, 16.5, { align: "right" });
        T(F.body, "normal", 6.3, SLATE); pdf.text(`${q.customer_title || ""}  ·  ${fmtDate(q.date)}  ·  devam`, pageW - M - pdf.getTextWidth(no) - 4, 16.5, { align: "right" });
        fill(GOLD); pdf.rect(M, HEAD2 - 1.6, W, 0.9, "F");
      }
      drawFooter();
    };

    // ---------- Sayfa 1: müşteri + teklif bilgileri (tek açık kart, ayraçlı sütunlar) ----------
    drawHeader(1);
    const y0 = HEAD1 + STRIP + (compact ? 4.5 : 6), rowH = compact ? 21 : 24, custW = 72;
    fill(WASH); pdf.roundedRect(M, y0, W, rowH, 3, 3, "F");
    fill(WHITE); pdf.circle(M + 9, y0 + rowH / 2, 7.4 * k, "F");
    icon("user", M + 9 - 3.7 * k, y0 + rowH / 2 - 3.7 * k, 7.4 * k);
    const tx = M + 20.5, nameW = custW - 22;
    T(F.body, "bold", 6.3, SLATE); pdf.text("SAYIN MÜŞTERİMİZ", tx, y0 + 6.6, { charSpace: 0.7 });
    const title = String(q.customer_title || "");
    let nfs = compact ? 10.5 : 11.5, nameLines = [title];
    for (const fs of [nfs, 10.5, 9.5, 8.8]) { nfs = fs; T(F.head, "bold", fs, NAVY); nameLines = pdf.splitTextToSize(title, nameW); if (nameLines.length <= 2) break; }
    if (nameLines.length > 2) { nfs = 8.2; T(F.head, "bold", nfs, NAVY); nameLines = pdf.splitTextToSize(title, nameW).slice(0, 3); }
    T(F.head, "bold", nfs, NAVY);
    if (nameLines.length === 1) {
      pdf.text(title, tx, y0 + 13.2);
      fill(GOLD); pdf.rect(tx, y0 + 15.9, 11, 0.9, "F");
      const meta = [q.customer_phone ? `Tel: ${q.customer_phone}` : "", q.customer_address ? String(q.customer_address) : ""].filter(Boolean).join("   ·   ");
      if (meta) { T(F.body, "normal", 6.6, SLATE); pdf.text(ellipsize(meta, nameW), tx, y0 + rowH - 2.6); }
    } else {
      const lhN = nfs * 0.3528 * 1.15, ny = nameLines.length === 3 ? y0 + 10 : y0 + 11.2;
      pdf.text(nameLines, tx, ny, { lineHeightFactor: 1.15 });
      fill(GOLD); pdf.rect(tx, ny + lhN * (nameLines.length - 1) + 2.2, 11, 0.9, "F");
    }
    const xs = M + custW;
    // Teklif bilgileri: 3 beyaz kart (altın kenar şeridi · ikon + etiket · değer) — okunaklı, modern
    const metaW = W - custW, cardGap = 3, cardH = compact ? 14 : 17, cardW = (metaW - 4 - 3 - cardGap * 2) / 3, cardY = y0 + (rowH - cardH) / 2;
    const validStr = q.valid_until ? fmtDate(q.valid_until) : "1 iş günü";
    const metaCells = [["file", "TEKLİF NO", String(q.quote_no || "")], ["calendar", "TEKLİF TARİHİ", fmtDate(q.date)], ["clock", "GEÇERLİLİK", validStr]];
    const vfs = Math.min(...metaCells.map(([, , val]) => shrink(val, cardW - 9, F.head, "bold", compact ? 9.4 : 10.4, 6.5)));
    metaCells.forEach(([ic, label, val], i) => {
      const cx = xs + 4 + i * (cardW + cardGap), lblY = cardY + (compact ? 5.4 : 6.2);
      fill(WHITE); pdf.roundedRect(cx, cardY, cardW, cardH, 2, 2, "F");
      fill(GOLD); pdf.roundedRect(cx + 2, cardY + 3, 1.3, cardH - 6, 0.6, 0.6, "F");
      icon(ic, cx + 5.6, lblY - 3.1, 3.8);
      T(F.body, "bold", 5.8, SLATE); pdf.text(label, cx + 10.6, lblY, { charSpace: 0.3 });
      T(F.head, "bold", vfs, NAVY); pdf.text(val, cx + 5.6, cardY + cardH - (compact ? 3.4 : 4.2));
    });

    // ---------- Kalem tablosu ----------
    const cols = [{ key: "idx", label: "#", w: 7, align: "center", st: { textColor: SLATE } }];
    if (showCodes) cols.push({ key: "code", label: "ÜRÜN KODU", w: 23, align: "center", st: { font: F.mono, fontSize: base - 0.8, textColor: TEXT } });
    cols.push({ key: "name", label: "ÜRÜN AÇIKLAMASI", w: null, align: "left", st: { fontStyle: "bold" } });
    cols.push({ key: "qty", label: "MİKTAR", w: 14, align: "center", st: {} });
    cols.push({ key: "unit", label: "BİRİM", w: 13, align: "center", st: { textColor: SLATE, fontSize: base - 0.8 } });
    cols.push({ key: "price", label: "BİRİM FİYAT", w: 23, align: "right", st: {} });
    if (detailed) {
      cols.push({ key: "disc", label: "İSK.", w: 15.5, align: "center", st: { fontStyle: "bold", textColor: NAVY } });
      cols.push({ key: "net", label: "NET FİYAT", w: 23, align: "right", st: {} });
    }
    cols.push({ key: "total", label: "TUTAR", w: 26, align: "right", st: { fontStyle: "bold", textColor: NAVY } });
    const nameIdx = cols.findIndex((c) => c.key === "name");
    const fixedW = cols.reduce((s, c) => s + (c.w || 0), 0);
    const pad = compact ? 0.6 : 1.4;
    const columnStyles = {};
    cols.forEach((c, i) => { columnStyles[i] = { cellWidth: c.w == null ? W - fixedW : c.w, halign: c.align, cellPadding: { top: pad, bottom: pad, left: 1.6, right: 1.6 }, ...c.st }; });
    const body = items.map((it, i) => {
      const netUnit = (Number(it.unit_price) || 0) * (1 - (Number(it.discount_rate) || 0) / 100);
      const line = (Number(it.quantity) || 0) * netUnit;
      const cell = {
        idx: String(i + 1), code: String(it.code || "").trim() || "—",
        name: [itemDisplayName(it, { showBrand }), ...itemSubLines(it, { showCodes: false }).filter((s) => !it.code || norm(s) !== norm(it.code))].join("\n"),
        qty: NUM(it.quantity), unit: unitLabel(it.unit), price: money(detailed ? it.unit_price : netUnit),
        disc: Number(it.discount_rate) ? `%${NUM(it.discount_rate)}` : "—", net: money(netUnit), total: money(line),
      };
      return cols.map((c) => cell[c.key]);
    });
    const head = [cols.map((c) => ({ content: c.label, styles: { halign: c.align } }))];
    const rowH2 = base * (compact ? 0.72 : 1.1);
    const subFs = base - 1.3;
    autoTable(pdf, {
      head, body: body.length ? body : [[{ content: "Kalem bulunmuyor", colSpan: cols.length, styles: { halign: "center", textColor: SLATE } }]],
      startY: y0 + rowH + (compact ? 4 : 6), margin: { left: M, right: M, top: HEAD2 + 5, bottom: FOOT + 5 }, rowPageBreak: "avoid", theme: "grid",
      styles: { font: F.body, fontSize: base, cellPadding: { top: pad, bottom: pad, left: 2, right: 2 }, textColor: INK, lineColor: LINE, lineWidth: 0.2, overflow: "linebreak", valign: "middle", minCellHeight: rowH2 },
      headStyles: { font: F.body, fontStyle: "bold", fillColor: NAVY, textColor: WHITE, fontSize: base - 0.6, valign: "middle", minCellHeight: 9.6 * k, lineWidth: 0, cellPadding: { top: 2 * k, bottom: 2 * k, left: 1.6, right: 1.6 } },
      alternateRowStyles: { fillColor: [250, 251, 253] }, columnStyles,
      // Açıklama hücresi elle çizilir: ürün adı kalın, açıklama alt satırı küçük ve gri
      willDrawCell: (d) => { if (d.section === "body" && d.column.index === nameIdx && typeof d.cell.raw === "string") { const parts = d.cell.raw.split("\n"); d.cell._name = parts[0]; d.cell._subs = parts.slice(1); d.cell.text = []; } },
      didDrawCell: (d) => {
        if (d.section === "head" && d.column.index > 0) { fill([44, 68, 102]); pdf.rect(d.cell.x, d.cell.y + 2.4 * k, 0.25, d.cell.height - 4.8 * k, "F"); }
        if (d.cell._name == null) return;
        const { x, y, width, height } = d.cell; const padL = d.cell.padding("left"), avail = width - padL - d.cell.padding("right");
        T(F.body, "bold", base, INK); const nameLs = pdf.splitTextToSize(d.cell._name, avail);
        T(F.body, "normal", subFs, SLATE); const subLs = d.cell._subs.flatMap((s) => pdf.splitTextToSize(s, avail));
        const lh = base * 0.42, slh = subFs * 0.42;
        let ty = y + (height - (nameLs.length * lh + subLs.length * slh)) / 2 + lh * 0.78;
        T(F.body, "bold", base, INK); nameLs.forEach((ln) => { pdf.text(ln, x + padL, ty); ty += lh; });
        T(F.body, "normal", subFs, SLATE); subLs.forEach((ln) => { pdf.text(ln, x + padL, ty); ty += slh; });
      },
      didDrawPage: (d) => drawHeader(d.pageNumber),
    });

    // ---------- Açıklamalar + Toplamlar ----------
    let y = pdf.lastAutoTable.finalY + 5 * k;
    const ensure = (need) => { if (y + need > pageH - FOOT - 3) { pdf.addPage(); drawHeader(pdf.getNumberOfPages()); y = HEAD2 + 7; } };
    const leftW = 110, rightX = M + leftW + 5, rightW = W - leftW - 5;
    const cf = compact ? 6.6 : 7.4, clh = cf * 0.5, headH = 12 * k, itemGap = 2.1 * k;
    T(F.body, "normal", cf, TEXT);
    const condLines = conds.map((c) => pdf.splitTextToSize(c, leftW - 19));
    const condsH = headH + 4.5 + condLines.reduce((s, w) => s + w.length * clh + itemGap, 0) + 2.5;
    // Özel not: açıklamaların altında altın kutu (gözden kaçmaz)
    T(F.body, "bold", cf + 0.3, NAVY);
    const noteLines = String(q.note || "").trim() ? pdf.splitTextToSize(String(q.note).trim(), leftW - 12) : null;
    const noteH = noteLines ? noteLines.length * clh * 1.06 + 10.5 * k : 0;
    const leftH = condsH + (noteLines ? 3 * k + noteH : 0);
    const tRows = [["ARA TOPLAM", money(q.subtotal)]];
    if (Number(q.global_discount_rate) > 0) { tRows.push([`GENEL İSKONTO (%${NUM(q.global_discount_rate)})`, `- ${money(q.discount)}`]); tRows.push(["NET TUTAR", money(q.net)]); }
    tRows.push([`KDV (%${NUM(rate)})`, kdvOn ? money(q.kdv) : "Hariç"]);
    const rh = 9.5 * k, gap = 2 * k, gtH = 17.5 * k, totalsH = tRows.length * (rh + gap) + gtH;
    ensure(Math.max(leftH, totalsH));
    const top = y;
    fill(WASH); pdf.roundedRect(M, top, leftW, condsH, 2.5, 2.5, "F");
    icon("scroll", M + 5, top + 3.4 * k, 7 * k);
    T(F.head, "bold", 10.5 * k + 0.5, NAVY); pdf.text("AÇIKLAMALAR", M + 14.5, top + 8.7 * k, { charSpace: 0.3 });
    let ly = top + headH + 4.5;
    condLines.forEach((wrapped, i) => {
      fill(GOLD); pdf.circle(M + 8, ly - 1.25, 2.2 * k, "F");
      T(F.body, "bold", 6 * k, WHITE); pdf.text(String(i + 1), M + 8, ly - 1.25 + 0.78 * k, { align: "center" });
      T(F.body, "normal", cf, TEXT); pdf.text(wrapped, M + 13, ly, { lineHeightFactor: 1.42 });
      ly += wrapped.length * clh + itemGap;
    });
    if (noteLines) {
      const ny = top + condsH + 3 * k;
      fill(GOLD); pdf.roundedRect(M, ny, leftW, noteH, 2.5, 2.5, "F");
      T(F.body, "bold", 6.4 * k + 0.4, NAVY); pdf.text("ÖZEL NOT", M + 6, ny + 5.2 * k, { charSpace: 0.6 });
      T(F.body, "bold", cf + 0.3, NAVY); pdf.text(noteLines, M + 6, ny + 9.8 * k, { lineHeightFactor: 1.42 });
    }
    let ty = top;
    tRows.forEach(([label, val]) => {
      fill(WASH); pdf.roundedRect(rightX, ty, rightW, rh, 2, 2, "F");
      T(F.body, "normal", 7.8 * k + 0.4, INK); pdf.text(label, rightX + 5, ty + rh * 0.64, { charSpace: 0.2 });
      T(F.body, "bold", 8.8 * k + 0.4, NAVY); pdf.text(val, rightX + rightW - 5, ty + rh * 0.64, { align: "right" });
      ty += rh + gap;
    });
    fill(NAVY); pdf.roundedRect(rightX, ty, rightW, gtH, 2.5, 2.5, "F");
    T(F.head, "bold", 9.6 * k, WHITE); pdf.text("GENEL TOPLAM", rightX + 5, ty + gtH / 2 + 1.6 * k);
    const labelW = pdf.getTextWidth("GENEL TOPLAM");
    const amt = money(q.total);
    const afs = shrink(amt, rightW - 10 - labelW - 3, F.head, "bold", 14.5 * k, 8);
    T(F.head, "bold", afs, GOLD); pdf.text(amt, rightX + rightW - 5, ty + 9.4 * k, { align: "right" });
    T(F.body, "bold", 6.2 * k + 0.3, [203, 213, 225]); pdf.text(kdvOn ? "KDV DAHİL" : "+ KDV", rightX + rightW - 5, ty + gtH - 3.2 * k, { align: "right", charSpace: 0.3 });
    y = top + Math.max(leftH, totalsH) + 6 * k;

    // ---------- Güven rozetleri (her zaman görünür; kompakt ve modern) ----------
    const badges = [["truck", "HIZLI", "TEDARİK"], ["box", "GENİŞ ÜRÜN", "YELPAZESİ"], ["shield", "ORİJİNAL", "ÜRÜNLER"], ["headset", "TEKNİK", "DESTEK"]];
    const isz = 7.6 * k;
    ensure(isz + 9 * k);
    stroke(NAVY, 0.5); pdf.line(M, y, pageW - M, y);
    y += 4 * k;
    const cw2 = W / badges.length;
    T(F.head, "normal", 7 * k, NAVY);
    badges.forEach(([ic, l1, l2], i) => {
      const cx = M + i * cw2;
      const textW = Math.max(pdf.getTextWidth(l1), pdf.getTextWidth(l2));
      const sx = cx + (cw2 - (isz + 2.8 + textW)) / 2;
      icon(ic, sx, y, isz);
      pdf.text(l1, sx + isz + 2.8, y + isz * 0.42); pdf.text(l2, sx + isz + 2.8, y + isz * 0.86);
      if (i > 0) { stroke(LINE, 0.35); pdf.line(cx, y + 0.4, cx, y + isz - 0.4); }
    });
    y += isz + 4 * k;
    fill(GOLD); pdf.rect(M, y, W, 0.9, "F");
    y += 6 * k;

    // ---------- Teşekkür ----------
    if (y + 8 * k <= pageH - FOOT - 4) {
      T(F.head, "normal", 9.4 * k, NAVY);
      pdf.text("Bizi tercih ettiğiniz için teşekkür ederiz.", M, y + 4 * k);
      fill(GOLD); pdf.rect(M, y + 6.8 * k, 14, 0.9, "F");
    }

    // ---------- Sayfa numaraları ----------
    const n = pdf.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      pdf.setPage(i);
      T(F.body, "normal", 6, SLATE); pdf.text(`Sayfa ${i} / ${n}`, pageW - M, pageH - FOOT + 8.7, { align: "right" });
    }
    return pdf;
  };

  if (singlePage) {
    let pdf;
    for (const [fs, compact] of FIT_STEPS) { pdf = await render(fs, compact); if (pdf.getNumberOfPages() === 1) break; }
    return pdf;
  }
  return render(8, false);
}

// Teklif dosya adı: cari adının ilk iki kelimesi + teklif no (ör. "ÇAKIR ELEKTRİK TKF-2026-377")
export const quoteFileName = (q) => docFileName(q?.customer_title, q?.quote_no || "Teklif", "");

/** PDF'i gizli iframe'de yazdır (indirme ile birebir aynı çıktı). Pop-up engelinde yeni sekmede açar.
 *  asFileName: yazdır → "PDF olarak kaydet" önerilen dosya adı = title. */
export function printPdf(pdf, title = "Belge", { asFileName = false } = {}) {
  if (asFileName) {
    try { pdf.setProperties({ title }); } catch { /* noop */ }
    const prev = document.title;
    document.title = title;
    const restore = () => { document.title = prev; };
    window.addEventListener("focus", () => setTimeout(restore, 500), { once: true });
    setTimeout(restore, 60000);
  }
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  frame.title = title;
  frame.src = url;
  document.body.appendChild(frame);
  let printed = false;
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); printed = true; }
    catch { window.open(url, "_blank"); }
    setTimeout(() => { try { document.body.removeChild(frame); URL.revokeObjectURL(url); } catch { /* noop */ } }, 60000);
  };
  setTimeout(() => { if (!printed) { try { window.open(url, "_blank"); } catch { /* noop */ } } }, 2500);
}
